import type {
  ValidationError,
  EFilingWizardState,
} from '@lds-efsp/shared-contracts';
import type { BlockingSurface } from '../components/BlockingMessage';

/**
 * SPEC-SOR-321 R8 — the ONE decider tying a populated blocking message's items
 * to the single form control each names. An item that resolves to a control
 * marks that control `aria-invalid` and points `aria-describedby` at the item,
 * so a filer who tabs into the field hears the problem again without returning
 * to the summary; both attributes disappear the moment no item names the field.
 *
 * Pure and dependency-light (type-only imports): the R8 property shard imports
 * this module in a bare process, so nothing here may pull React in.
 */

/**
 * The ONE surface literal whose item ids an `aria-describedby` names. The
 * validation banner and next-blockers render the SAME
 * `getTabValidationErrors(currentTab)` items on the same step, so item ids must
 * be surface-scoped (no duplicate DOM ids) and the aria pointer must name the
 * banner copy — the item that is in the document whenever the attribute is set,
 * because both sides derive from the same context state in the same render.
 * `offendingFieldAria` and the wizard's banner `<li>` both build ids through
 * this constant, so the pointer and the element can never drift to different
 * surfaces.
 */
export const CANONICAL_BLOCKER_SURFACE: BlockingSurface = 'validation-banner';

/**
 * DOM id a blocking-message item carries. Composes the two id conventions it
 * replaces — BlockingMessage's container `id={surface}` and the wizard's former
 * React keys `${err.field}-${err.rule}` — so an item is addressable from an
 * `aria-describedby`. `err.field` values like `documents[0].documentType` are
 * legal in an HTML id but poison CSS querySelectors, so the field is slugified;
 * tests locate items by `getElementById`, which is safe either way. (Slug
 * collisions — `a[0]` vs `a-0` — are theoretically possible but unreachable:
 * the validator's field vocabulary holds no such pair.)
 */
export function blockerItemId(
  surface: BlockingSurface,
  error: Pick<ValidationError, 'field' | 'rule'>,
): string {
  const slug = error.field.replace(/[^A-Za-z0-9_-]/g, '-');
  return `blocker-${surface}-${slug}-${error.rule}`;
}

/**
 * Which single form control (if any) a blocking item names — the ONE decider
 * for that question. DEFAULT-DENY: only the four rules whose fault is a single
 * fillable control resolve to it; document-, order- and section-level items
 * (`min-documents`, `lead-document-required`, `single-lead-document`,
 * `case-attach-required`, `case-county-mismatch`, `case-binding-absent`, the
 * four `*-section-required`, and anything added later) resolve `null`, and an
 * unknown rule must never mark a field.
 *
 * The doc-type leg resolves from `state.documents`, never by parsing
 * `err.field` — the validation field is a positional index (SOR-160), so the
 * jump target is the FIRST typeless document's combobox
 * (`doc-type-combobox-<docId>`; the id names the card wrapper, which is what
 * the jump scrolls to). `null` when no document is typeless, preserving the
 * static fallback. The field legs lean on the FormInput/FormSelect convention
 * that a control's `id` equals its `name`.
 */
export function resolveOffendingFieldId(
  error: Pick<ValidationError, 'rule' | 'field'>,
  state: Pick<EFilingWizardState, 'documents'>,
): string | null {
  switch (error.rule) {
    case 'doc-type-required': {
      const typeless = state.documents.find(d => !d.documentType);
      return typeless ? `doc-type-combobox-${typeless.id}` : null;
    }
    case 'case-title-required':
      return 'caseTitle';
    case 'incident-zip-required':
      return 'incidentZip';
    case 'county-required':
      return 'county';
    default:
      return null;
  }
}

/** The R8 attribute pair, or `{}` while no item names the control. */
export type OffendingFieldAria = {
  'aria-invalid'?: true;
  'aria-describedby'?: string;
};

/**
 * The attributes a control carries while some item of `items` resolves to it:
 * `aria-invalid` plus an `aria-describedby` naming EVERY resolving item's
 * `blockerItemId` (space-joined, banner-scoped — see
 * `CANONICAL_BLOCKER_SURFACE`). Empty otherwise, which is the "returns to valid
 * when no item remains" half of R8: the pair is recomputed from context state
 * on every render, so it holds by construction.
 */
export function offendingFieldAria(
  controlId: string,
  items: ValidationError[],
  state: Pick<EFilingWizardState, 'documents'>,
): OffendingFieldAria {
  const resolving = items.filter(
    item => resolveOffendingFieldId(item, state) === controlId,
  );
  if (resolving.length === 0) return {};
  return {
    'aria-invalid': true,
    'aria-describedby': resolving
      .map(item => blockerItemId(CANONICAL_BLOCKER_SURFACE, item))
      .join(' '),
  };
}
